import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ---------------- helpers ---------------- */
const sameId = (a, b) => String(a || "") === String(b || "");
const getId = (v) => (v && typeof v === "object" ? v._id || v.id || v.value : v);

const matchesFilters = (item, args = {}) => {
  const { shape = "", q = "", dateFrom = "", dateTo = "" } = args || {};

  if (shape && !sameId(getId(item.shape), shape)) return false;

  if (q) {
    const needle = String(q).trim().toLowerCase();
    const hay = String(item.sieve || "").toLowerCase();
    if (!hay.includes(needle)) return false;
  }

  if (dateFrom && new Date(item.createdAt) < new Date(dateFrom)) return false;
  if (dateTo && new Date(item.createdAt) > new Date(dateTo)) return false;

  return true;
};

const addItemsIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.data)) return;
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newItems.length);
    return;
  }
  draft.data.unshift(...newItems);
  if (args.limit) draft.data.splice(Number(args.limit));
  incrementTotals(draft, newItems.length);
};

const incrementTotals = (draft, by = 1) => {
  if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || 20;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};
const decrementTotals = (draft, by = 1) => {
  if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || 20;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

export const diamondSieveApi = createApi({
  reducerPath: "diamondSieveApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["DiamondSieve"],
  endpoints: (builder) => ({
    // LIST
    getDiamondSieves: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 20,
          shape = "",
          q = "",
          sortBy,
          sortOrder,
          dateFrom,
          dateTo,
        } = args;

        const params = { page, limit };
        if (shape) params.shape = shape;
        if (q) params.q = q;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;

        return { url: "/diamond-sieves", method: "GET", params };
      },
      providesTags: ["DiamondSieve"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.shape,
          a.q,
          a.sortBy,
          a.sortOrder,
          a.dateFrom,
          a.dateTo,
        ].join("|");
      },
      keepUnusedDataFor: 60,
    }),

    // GET ONE
    getDiamondSieveById: builder.query({
      query: (id) => `/diamond-sieves/${id}`,
      providesTags: (_res, _err, id) => [{ type: "DiamondSieve", id }],
    }),

    // CREATE
    createDiamondSieve: builder.mutation({
      query: (body) => ({
        url: `/diamond-sieves/create`,
        method: "POST",
        body,
      }),
      async onQueryStarted(_payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = res?.data ? (Array.isArray(res.data) ? res.data : [res.data]) : [];
          if (!created.length) return;

          const allArgs = diamondSieveApi.util.selectCachedArgsForQuery(
            getState(),
            "getDiamondSieves"
          );
          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;

            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                addItemsIntoListDraft(draft, args, addable);
              })
            );
          }
        } catch {}
      },
    }),

    // UPDATE
    updateDiamondSieve: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/diamond-sieves/${id}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = diamondSieveApi.util.selectCachedArgsForQuery(
          getState(),
          "getDiamondSieves"
        );

        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const idx = draft.data.findIndex((x) => String(x._id) === String(id));
                if (idx >= 0) {
                  const next = { ...draft.data[idx], ...patch };
                  if (matchesFilters(next, args)) {
                    draft.data[idx] = next;
                  } else {
                    draft.data.splice(idx, 1);
                    decrementTotals(draft, 1);
                  }
                } else {
                  const phantom = { _id: id, ...patch };
                  if ((args.page ?? 1) === 1 && matchesFilters(phantom, args)) {
                    draft.data.unshift(phantom);
                    incrementTotals(draft, 1);
                    if (args.limit) draft.data.splice(Number(args.limit));
                  }
                }
              })
            )
          );
        }

        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data;
          if (!updated) return;

          for (const args of allArgs) {
            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const idx = draft.data.findIndex((x) => String(x._id) === String(id));
                if (idx >= 0) {
                  if (matchesFilters(updated, args)) {
                    draft.data[idx] = { ...draft.data[idx], ...updated };
                  } else {
                    draft.data.splice(idx, 1);
                    decrementTotals(draft, 1);
                  }
                } else if ((args.page ?? 1) === 1 && matchesFilters(updated, args)) {
                  draft.data.unshift(updated);
                  incrementTotals(draft, 1);
                  if (args.limit) draft.data.splice(Number(args.limit));
                }
              })
            );
          }
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    // DELETE
    deleteDiamondSieve: builder.mutation({
      query: (id) => ({ url: `/diamond-sieves/${id}`, method: "DELETE" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = diamondSieveApi.util.selectCachedArgsForQuery(
          getState(),
          "getDiamondSieves"
        );
        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((x) => String(x._id) !== String(id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    // ACTIVATE
    activateDiamondSieve: builder.mutation({
      query: (id) => ({ url: `/diamond-sieves/${id}/activate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = diamondSieveApi.util.selectCachedArgsForQuery(
          getState(),
          "getDiamondSieves"
        );
        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                const row = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (row) row.isActive = true;
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    // DEACTIVATE
    deactivateDiamondSieve: builder.mutation({
      query: (id) => ({ url: `/diamond-sieves/${id}/deactivate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const rollbacks = [];
        const allArgs = diamondSieveApi.util.selectCachedArgsForQuery(
          getState(),
          "getDiamondSieves"
        );
        for (const args of allArgs) {
          rollbacks.push(
            dispatch(
              diamondSieveApi.util.updateQueryData("getDiamondSieves", args, (draft) => {
                const row = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (row) row.isActive = false;
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          rollbacks.forEach((p) => p?.undo && p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetDiamondSievesQuery,
  useGetDiamondSieveByIdQuery,
  useCreateDiamondSieveMutation,
  useUpdateDiamondSieveMutation,
  useDeleteDiamondSieveMutation,
  useActivateDiamondSieveMutation,
  useDeactivateDiamondSieveMutation,
} = diamondSieveApi;

export default diamondSieveApi;
